import React, { useCallback, useEffect, useState } from 'react';
import { calendarApi } from './api';
import { todayWall, wallDate } from './calendarTime';
import { CHANGED_EVENT } from './calendarToday';
import { openClientInMain, openFormInAcord, formHref } from './calendarFollowup';
import { useCloseGuard } from './calendarModal';
import NotesReader, { ExpandButton } from './NotesReader';

// 5.32.0 Tasks tab: the viewed person's task list (their HDCal VTODO calendars — "Tasks" and any other of their
// own). Add, edit title / notes / due date, complete / un-complete, delete. Completed tasks fold away.
// Every change sends the ETag it was based on; "changed elsewhere" reloads the list.
// 5.43.0: the editor closes only by Save / Cancel (Esc does nothing); Cancel after a change asks "Discard changes?".
// "⤢ Expand" on notes opens them full-page (NotesReader); saving there is the normal task save (notes only).

const C = { bg: '#1a1a1a', panel: '#242424', border: '#333', text: '#f0f0f0', dim: '#aaa', violet: '#7c3aed' };
const input = { padding: '7px 9px', border: `1px solid ${C.border}`, borderRadius: 6, background: C.bg, color: C.text, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' };
const small = (bg) => ({ padding: '4px 10px', border: 'none', borderRadius: 5, background: bg, color: '#fff', fontSize: 12, cursor: 'pointer' });

const fmtDue = new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });

function TaskRow({ t, onToggle, onSave, onDelete, busy }) {
  const [edit, setEdit] = useState(false);
  const [title, setTitle] = useState(t.title);
  const [notes, setNotes] = useState(t.notes);
  const [due, setDue] = useState(t.due ? wallDate(t.due) : '');
  useEffect(() => { setTitle(t.title); setNotes(t.notes); setDue(t.due ? wallDate(t.due) : ''); }, [t]);
  const overdue = !t.completed && t.due && wallDate(t.due) < todayWall();
  const [reader, setReader] = useState(false);
  const dirty = edit && (title !== t.title || notes !== t.notes || due !== (t.due ? wallDate(t.due) : ''));
  const stopEdit = () => { setTitle(t.title); setNotes(t.notes); setDue(t.due ? wallDate(t.due) : ''); setEdit(false); };
  const { requestClose, prompt } = useCloseGuard(dirty, stopEdit);
  const save = () => {
    const ch = {};
    if (title !== t.title) ch.title = title;
    if (notes !== t.notes) ch.notes = notes;
    if (due !== (t.due ? wallDate(t.due) : '')) ch.due = due;
    if (Object.keys(ch).length) onSave(t, ch).then((ok) => { if (ok) setEdit(false); }); else setEdit(false);
  };
  return (
    <div data-testid="cal-task" style={{ borderBottom: `1px solid ${C.border}`, padding: '8px 4px', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
      <input type="checkbox" checked={!!t.completed} disabled={busy || !t.can_edit} onChange={() => onToggle(t)}
        aria-label={t.completed ? 'Mark not done' : 'Mark done'} style={{ marginTop: 3, width: 16, height: 16, cursor: 'pointer' }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        {edit ? (
          <div style={{ display: 'grid', gap: 6 }}>
            <input value={title} onChange={(e) => setTitle(e.target.value)} style={input} maxLength={500} autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter') save(); }} />
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Notes" style={{ ...input, resize: 'vertical' }} />
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, color: C.dim }}>Due</span>
              <input type="date" value={due} onChange={(e) => setDue(e.target.value)} style={input} />
              {due && <button type="button" style={small('#444')} onClick={() => setDue('')}>No due date</button>}
              <div style={{ flex: 1 }} />
              <button type="button" style={small(C.violet)} disabled={busy} onClick={save}>Save</button>
              <button type="button" style={small('#444')} disabled={busy} onClick={requestClose} data-testid="cal-task-cancel">Cancel</button>
            </div>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 14, color: t.completed ? C.dim : C.text, textDecoration: t.completed ? 'line-through' : 'none', wordBreak: 'break-word' }}>{t.title || '(no title)'}</div>
            {t.notes && (
              <div style={{ fontSize: 12, color: C.dim, whiteSpace: 'pre-wrap', marginTop: 2 }}>
                {t.notes} <ExpandButton onClick={() => setReader(true)} testid="cal-task-expand" />
              </div>
            )}
            <div style={{ fontSize: 12, marginTop: 2, color: overdue ? '#f87171' : C.dim }}>
              {t.due ? `Due ${fmtDue.format(new Date(`${wallDate(t.due)}T00:00:00Z`))}${overdue ? ' — overdue' : ''}` : ''}
              {t.calendar && t.calendar.kind !== 'tasks' ? `${t.due ? ' · ' : ''}${t.calendar.name}` : ''}
            </div>
            {formHref(t.form_type, t.form_id) && (     /* 5.42.0 follow-up from an ACORD form */
              <button type="button" onClick={() => openFormInAcord(t.form_type, t.form_id)} data-testid="task-open-form"
                style={{ ...small('#0f766e'), marginTop: 4, marginRight: 4 }}>📝 Open form</button>
            )}
            {t.client_id && (     /* 5.38.0 client follow-up */
              <button type="button" onClick={() => openClientInMain(t.client_id)} data-testid="task-open-client"
                style={{ ...small('#7c3aed'), marginTop: 4 }}>📂 Open client</button>
            )}
          </>
        )}
      </div>
      {reader && (
        <NotesReader title={t.title} canEdit={!!t.can_edit} notes={t.notes}
          when={t.due ? `Due ${fmtDue.format(new Date(`${wallDate(t.due)}T00:00:00Z`))} (Central)` : 'No due date'}
          onSave={(text) => onSave(t, { notes: text })} onClose={() => setReader(false)} />
      )}
      {prompt}
      {!edit && t.can_edit && (
        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
          <button type="button" style={small('#334155')} disabled={busy} onClick={() => setEdit(true)}>Edit</button>
          <button type="button" style={small('#7f1d1d')} disabled={busy} onClick={() => { if (window.confirm('Delete this task?')) onDelete(t); }} aria-label="Delete task">✕</button>
        </div>
      )}
    </div>
  );
}

export default function CalendarTasks({ target, enabled }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [showDone, setShowDone] = useState(false);
  const [nt, setNt] = useState({ title: '', due: '', notes: '' });

  const load = useCallback(async () => {
    try { const r = await calendarApi.tasks(target); setData(r.data); } catch (e) { setErr(e?.response?.data?.detail || 'Could not load tasks'); }
  }, [target]);
  useEffect(() => { setData(null); setErr(''); load(); }, [load]);
  useEffect(() => {                         // 5.34.0: a task ticked in the Today panel shows here at once
    const on = () => load();
    window.addEventListener(CHANGED_EVENT, on);
    return () => window.removeEventListener(CHANGED_EVENT, on);
  }, [load]);

  const act = async (fn) => {
    setBusy(true); setErr('');
    try { await fn(); await load(); return true; } catch (e) {
      const d = e?.response?.data;
      setErr(d?.detail || 'Something went wrong');
      if (e?.response?.status === 409) await load();
      return false;
    } finally { setBusy(false); }
  };

  if (!enabled) return <div style={{ color: C.dim, padding: 20 }}>No task list — this login has no personal calendar (Office only).</div>;
  if (!data) return <div style={{ color: C.dim, padding: 20 }}>{err || 'Loading tasks…'}</div>;

  const open = data.tasks.filter((t) => !t.completed);
  const done = data.tasks.filter((t) => t.completed);
  const add = () => {
    if (!nt.title.trim()) return;
    act(async () => { await calendarApi.createTask({ title: nt.title, due: nt.due || null, notes: nt.notes }, target); setNt({ title: '', due: '', notes: '' }); });
  };
  const rowProps = {
    busy,
    onToggle: (t) => act(() => calendarApi.update(t.object_id, t.etag, { completed: !t.completed }, target)),
    onSave: (t, ch) => act(() => calendarApi.update(t.object_id, t.etag, ch, target)),
    onDelete: (t) => act(() => calendarApi.remove(t.object_id, t.etag, target)),
  };

  return (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: '8px 4px 40px', color: C.text }} data-testid="cal-tasks">
      {data.tasks_calendar_id ? (
        <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 8, padding: 12, marginBottom: 14, display: 'grid', gap: 8 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input value={nt.title} onChange={(e) => setNt((p) => ({ ...p, title: e.target.value }))} placeholder="Add a task…" maxLength={500}
              onKeyDown={(e) => { if (e.key === 'Enter') add(); }} style={{ ...input, flex: '1 1 260px' }} data-testid="cal-task-new" />
            <input type="date" value={nt.due} onChange={(e) => setNt((p) => ({ ...p, due: e.target.value }))} style={input} title="Due date (optional)" />
            <button type="button" onClick={add} disabled={busy || !nt.title.trim()}
              style={{ ...small(C.violet), padding: '7px 16px', fontSize: 13, opacity: busy || !nt.title.trim() ? 0.5 : 1 }}>Add</button>
          </div>
          <input value={nt.notes} onChange={(e) => setNt((p) => ({ ...p, notes: e.target.value }))} placeholder="Notes (optional)" style={input} />
        </div>
      ) : (
        <div style={{ color: '#fbbf24', fontSize: 13, marginBottom: 14 }}>No Tasks list yet — it is created automatically within a minute once this person has a calendar account.</div>
      )}
      {err && <div style={{ background: '#7f1d1d', color: '#fecaca', padding: '8px 10px', borderRadius: 6, fontSize: 13, marginBottom: 10 }}>{err}</div>}
      <div style={{ fontSize: 12, color: C.dim, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>To do · {open.length}</div>
      {open.length === 0 && <div style={{ color: C.dim, fontSize: 13, padding: '8px 4px' }}>Nothing to do.</div>}
      {open.map((t) => <TaskRow key={t.object_id} t={t} {...rowProps} />)}
      {done.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <button type="button" onClick={() => setShowDone((s) => !s)} data-testid="cal-tasks-done-toggle"
            style={{ background: 'none', border: 'none', color: C.dim, fontSize: 12, cursor: 'pointer', padding: 0, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {showDone ? '▾' : '▸'} Completed · {done.length}
          </button>
          {showDone && done.map((t) => <TaskRow key={t.object_id} t={t} {...rowProps} />)}
        </div>
      )}
    </div>
  );
}
